import React, { useState, useEffect } from 'react';
import { backupAPI, importAPI, mediaUploadAPI } from '../services/api';
import './TasksPage.css';
import Toast from '../components/Toast';

function TasksPage() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [selectedTasks, setSelectedTasks] = useState(new Set());
  const [cancelling, setCancelling] = useState(false);
  const cancellingRef = React.useRef(false);

  const isCancellable = (task) => {
    if (task.type === 'backup' && ['PENDING', 'PROCESSING'].includes(task.status)) return true;
    if (task.type === 'import' && ['PROCESSING', 'VALIDATING', 'UPLOADING'].includes(task.status)) return true;
    return false;
  };

  const cancellableTasks = tasks.filter(isCancellable);

  const toggleSelect = (taskId) => {
    setSelectedTasks(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedTasks.size === cancellableTasks.length && cancellableTasks.length > 0) {
      setSelectedTasks(new Set());
    } else {
      setSelectedTasks(new Set(cancellableTasks.map(t => t.task_id)));
    }
  };

  const handleBatchCancel = async () => {
    const count = selectedTasks.size;
    if (count === 0) return;
    if (!window.confirm(`Cancel ${count} selected task${count > 1 ? 's' : ''}?`)) return;

    setCancelling(true);
    cancellingRef.current = true;
    setMessage({ type: 'info', text: `Cancelling ${count} task${count > 1 ? 's' : ''}... Please do not refresh the page.` });
    let succeeded = 0;
    let failed = 0;

    for (const taskId of selectedTasks) {
      const task = tasks.find(t => t.task_id === taskId);
      if (!task) { failed++; continue; }
      try {
        if (task.type === 'backup') {
          await backupAPI.cancel(taskId);
        } else if (task.type === 'import') {
          const cancelId = task.import_result_id || task.task_id;
          await importAPI.cancel(task.cemetery_unique_name, cancelId);
        }
        succeeded++;
      } catch {
        failed++;
      }
    }

    setSelectedTasks(new Set());
    setCancelling(false);
    cancellingRef.current = false;
    const parts = [];
    if (succeeded) parts.push(`${succeeded} cancelled`);
    if (failed) parts.push(`${failed} failed`);
    setMessage({ type: failed ? 'error' : 'success', text: `Batch cancel: ${parts.join(', ')}` });
    loadTasks();
  };

  useEffect(() => {
    loadTasks();
    // Refresh tasks every 5 seconds
    const interval = setInterval(() => {
      if (!cancellingRef.current) loadTasks();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadTasks = async () => {
    try {
      // Get active backup tasks
      const backupResponse = await backupAPI.getActiveTasks();

      // Celery inspect returns tasks grouped by worker: {'worker_name': [task1, task2]}
      // We need to flatten them into a single array
      const activeTasks = backupResponse.data.active_tasks || {};
      const backupTasks = [];

      // Flatten tasks from all workers
      Object.keys(activeTasks).forEach(workerName => {
        const workerTasks = activeTasks[workerName] || [];
        workerTasks.forEach(task => {
          backupTasks.push({
            task_id: task.id,
            cemetery_name: task.args?.[0] || task.kwargs?.cemetery_unique_name || 'Unknown',
            status: 'PROCESSING',
            type: 'backup',
            started_at: task.time_start ? new Date(task.time_start * 1000).toISOString() : null,
            current_operation: task.name || 'Processing...',
            worker: workerName,
          });
        });
      });

      // Get recent backup history (last 2 hours)
      const twoHoursAgo = new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString();
      const historyResponse = await backupAPI.getHistory({
        start_date: twoHoursAgo,
        page_size: 50,
      });

      const recentBackups = (historyResponse.data.backups || [])
        .filter(b => ['PENDING', 'PROCESSING', 'COMPLETED', 'FAILED'].includes(b.status))
        .map(backup => ({
          task_id: backup.task_id || `backup-${backup.id}`,
          backup_id: backup.id,
          cemetery_name: backup.cemetery_unique_name,
          status: backup.status,
          type: 'backup',
          started_at: backup.created_at,
          completed_at: backup.completed_at,
          download_url: backup.download_url,
          error_summary: backup.error_summary,
          file_size_mb: backup.file_size_mb,
        }));

      // Get active import tasks
      const importResponse = await importAPI.getActiveTasks();
      const importTasks = (importResponse.data.tasks || []).map(task => ({
        ...task,
        type: 'import',
        // Use import_result_id as fallback when task_id is null (UPLOADING tasks)
        task_id: task.task_id || `import-${task.import_result_id}`,
      }));

      // Get active media tasks (move + upload)
      let mediaTasks = [];
      try {
        const mediaResponse = await mediaUploadAPI.getActiveMediaTasks();
        mediaTasks = (mediaResponse.data.tasks || []).map(task => ({
          task_id: task.task_id,
          cemetery_name: task.cemetery_name || 'Unknown',
          status: task.state || task.status || 'UNKNOWN',
          type: task.type || 'media',
          progress: task.progress,
          current_operation: task.current_operation,
          started_at: task.started_at,
          completed_at: task.completed_at,
          files_copied: task.files_copied,
          files_total: task.files_total,
          files_uploaded: task.files_uploaded,
          files_failed: task.files_failed,
          error: task.error,
        }));
      } catch (mediaErr) {
        console.warn('Failed to load media tasks:', mediaErr);
      }

      // Combine all tasks - deduplicate backups (history might include active ones)
      const activeBackupIds = new Set(backupTasks.map(t => t.task_id));
      const uniqueRecentBackups = recentBackups.filter(b => !activeBackupIds.has(b.task_id));

      const allTasks = [...backupTasks, ...uniqueRecentBackups, ...importTasks, ...mediaTasks];
      setTasks(allTasks);
      setLoading(false);
    } catch (err) {
      console.error('Failed to load tasks:', err);
      setLoading(false);
    }
  };

  const handleCancelBackup = async (taskId) => {
    if (!window.confirm('Are you sure you want to cancel this backup?')) {
      return;
    }

    try {
      await backupAPI.cancel(taskId);
      setMessage({ type: 'success', text: 'Backup cancelled successfully' });
      loadTasks();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Failed to cancel backup',
      });
    }
  };

  const handleCancelImport = async (cemeteryName, taskId) => {
    if (!window.confirm('Are you sure you want to cancel this import?')) {
      return;
    }

    try {
      await importAPI.cancel(cemeteryName, taskId);
      setMessage({ type: 'success', text: 'Import cancelled successfully' });
      loadTasks();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Failed to cancel import',
      });
    }
  };

  const getStatusColor = (status) => {
    const statusMap = {
      PENDING: 'status-pending',
      PROCESSING: 'status-processing',
      COMPLETED: 'status-completed',
      FAILED: 'status-failed',
      CANCELLED: 'status-cancelled',
    };
    return statusMap[status] || 'status-unknown';
  };

  return (
    <div className="container">
      <div className="page-header">
        <div className="page-header-text">
          <h1>Task Monitor</h1>
          <p>Monitor active backup, import, and media operations</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={loadTasks} disabled={loading}>Refresh</button>
        </div>
      </div>

      <Toast message={message} onDismiss={() => setMessage(null)} />

      <div className="card">
        <div className="card-header">
          <div className="card-header-left">
            {cancellableTasks.length > 0 && (
              <input
                type="checkbox"
                className="task-checkbox"
                checked={selectedTasks.size === cancellableTasks.length && cancellableTasks.length > 0}
                onChange={toggleSelectAll}
                title="Select all cancellable tasks"
              />
            )}
            <h2>Active Tasks</h2>
          </div>
        </div>

        {loading && tasks.length === 0 ? (
          <div className="loading">Loading tasks...</div>
        ) : tasks.length === 0 ? (
          <div className="empty-state">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
            </svg>
            <p className="empty-title">No active tasks</p>
            <p className="empty-description">Tasks will appear here when backup or import operations are running</p>
          </div>
        ) : (
          <div className="tasks-list">
            {tasks.map((task) => (
              <div key={task.task_id} className={`task-item${selectedTasks.has(task.task_id) ? ' task-item-selected' : ''}`}>
                <div className="task-header">
                  {isCancellable(task) && (
                    <input
                      type="checkbox"
                      className="task-checkbox"
                      checked={selectedTasks.has(task.task_id)}
                      onChange={() => toggleSelect(task.task_id)}
                    />
                  )}
                  <div className="task-info">
                    <h3>
                      {task.type === 'backup' ? '📦 Backup' : task.type === 'import' ? '📥 Import' : task.type === 'media_move' ? '🔄 Media Move' : '📤 Media Upload'}:{' '}
                      {task.cemetery_name || task.cemetery_unique_name}
                    </h3>
                    <span className={`task-status ${getStatusColor(task.status)}`}>
                      {task.status}
                    </span>
                  </div>
                  <div className="task-actions">
                    {/* Only show cancel button for backup tasks in PENDING or PROCESSING state */}
                    {task.type === 'backup' && ['PENDING', 'PROCESSING'].includes(task.status) && (
                      <button
                        onClick={() => handleCancelBackup(task.task_id)}
                        className="btn btn-danger btn-sm"
                      >
                        Cancel
                      </button>
                    )}
                    {/* Only show cancel button for import tasks in PROCESSING, VALIDATING, or UPLOADING state */}
                    {task.type === 'import' && ['PROCESSING', 'VALIDATING', 'UPLOADING'].includes(task.status) && (
                      <button
                        onClick={() => handleCancelImport(task.cemetery_unique_name, task.task_id || task.import_result_id)}
                        className="btn btn-danger btn-sm"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>

                <div className="task-details">
                  <div className="detail-row">
                    <span className="detail-label">Task ID:</span>
                    <span className="detail-value">{task.task_id}</span>
                  </div>
                  {task.progress !== undefined && (
                    <div className="detail-row">
                      <span className="detail-label">Progress:</span>
                      <div className="progress-container">
                        <div className="progress-bar">
                          <div
                            className="progress-fill"
                            style={{ width: `${task.progress}%` }}
                          ></div>
                        </div>
                        <span className="progress-text">{task.progress}%</span>
                      </div>
                    </div>
                  )}
                  {task.current_operation && (
                    <div className="detail-row">
                      <span className="detail-label">Current Operation:</span>
                      <span className="detail-value">{task.current_operation}</span>
                    </div>
                  )}
                  {task.started_at && (
                    <div className="detail-row">
                      <span className="detail-label">Started:</span>
                      <span className="detail-value">
                        {new Date(task.started_at).toLocaleString()}
                      </span>
                    </div>
                  )}
                  {task.completed_at && (
                    <div className="detail-row">
                      <span className="detail-label">Completed:</span>
                      <span className="detail-value">
                        {new Date(task.completed_at).toLocaleString()}
                      </span>
                    </div>
                  )}
                  {task.file_size_mb && (
                    <div className="detail-row">
                      <span className="detail-label">File Size:</span>
                      <span className="detail-value">{task.file_size_mb.toFixed(2)} MB</span>
                    </div>
                  )}
                  {task.error_summary && (
                    <div className="detail-row error-row">
                      <span className="detail-label">Error:</span>
                      <span className="detail-value error-text">{task.error_summary}</span>
                    </div>
                  )}
                  {task.status === 'COMPLETED' && task.type === 'backup' && task.download_url && (
                    <div className="detail-row">
                      <a
                        href={task.download_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-success btn-sm"
                      >
                        Download Backup
                      </a>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedTasks.size > 0 && (
        <div className="batch-toolbar">
          <span className="batch-count">{selectedTasks.size} selected</span>
          <button
            className="btn btn-danger btn-sm"
            onClick={handleBatchCancel}
            disabled={cancelling}
          >
            {cancelling ? 'Cancelling...' : 'Cancel Selected'}
          </button>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => setSelectedTasks(new Set())}
            disabled={cancelling}
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );
}

export default TasksPage;
